import { type FC, useRef, JSX } from "react";
import { QrCode } from "../types";
import jsPDF from "jspdf";
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from "../context/AuthContext";
import { formatDate } from "../utils/utils";

// --- Icons ---
const Icons = {
    Close: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>,
    Download: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
    Hash: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>,
    Tag: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>,
    Calendar: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    User: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
    Clock: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    Activity: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
    Check: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
};

const DetailRow: FC<{ icon: JSX.Element; label: string; value: string | JSX.Element; highlight?: boolean }> = ({ icon, label, value, highlight }) => (
    <div className="flex justify-between items-center py-2.5 border-b border-slate-700/30 last:border-0">
        <div className="flex items-center text-slate-400 gap-2">
            {icon}
            <span className="text-sm font-medium">{label}</span>
        </div>
        <span className={`text-sm font-semibold text-right ${highlight ? 'text-sky-400' : 'text-slate-200'}`}>
            {value}
        </span>
    </div>
);

const QrCodeDisplayModal: FC<{ qrCode: QrCode; onClose: () => void }> = ({ qrCode, onClose }) => {
    const qrRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();

    const downloadAs = (format: 'png' | 'jpeg') => {
        if (!qrRef.current) return;

        const canvas = qrRef.current.querySelector('canvas');
        const svg = qrRef.current.querySelector('svg');

        const drawAndDownload = (imageSource: HTMLCanvasElement | HTMLImageElement, width: number, height: number) => {
            const padding = 40;
            const newCanvas = document.createElement('canvas');
            const newContext = newCanvas.getContext('2d');

            if (!newContext) return;

            newCanvas.width = width + padding * 2;
            newCanvas.height = height + padding * 2;

            newContext.fillStyle = '#FFFFFF';
            newContext.fillRect(0, 0, newCanvas.width, newCanvas.height);
            newContext.drawImage(imageSource, padding, padding);

            const link = document.createElement('a');
            link.download = `${qrCode.id}.qrcode-${qrCode.name.replace(/\s+/g, '_').toLowerCase()}.${format}`;
            link.href = newCanvas.toDataURL(`image/${format}`, 1.0);
            link.click();
        };

        if (canvas) {
            drawAndDownload(canvas, canvas.width, canvas.height);
        } else if (svg) {
            const svgString = new XMLSerializer().serializeToString(svg);
            const svgUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
            const img = new Image();
            img.onload = () => drawAndDownload(img, img.width, img.height);
            img.src = svgUrl;
        }
    };

    const downloadAsPdf = () => {
        if (!qrRef.current) return;
        const originalCanvas = qrRef.current.querySelector('canvas');
        if (!originalCanvas) return;

        const padding = 20;
        const newCanvas = document.createElement('canvas');
        const newContext = newCanvas.getContext('2d');
        const originalSize = originalCanvas.width;
        const newSize = originalSize + 2 * padding;

        newCanvas.width = newSize;
        newCanvas.height = newSize;

        if (newContext === null) return;
        newContext.fillStyle = '#FFFFFF';
        newContext.fillRect(0, 0, newSize, newSize);
        newContext.drawImage(originalCanvas, padding, padding);

        const imgData = newCanvas.toDataURL('image/png');
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: [newSize, newSize]
        });
        pdf.addImage(imgData, 'PNG', 0, 0, newSize, newSize);
        pdf.save(`${qrCode.id}.qrcode-${qrCode.name.replace(/\s+/g, '_').toLowerCase()}.pdf`);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="p-4 border-b border-slate-700/50 bg-slate-900/50 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white truncate pr-4">{qrCode.name}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700 transition-colors">
                        {Icons.Close}
                    </button>
                </div>

                <div className="overflow-y-auto custom-scrollbar flex-1">
                    {/* QR Code Area */}
                    <div className="p-8 bg-white flex justify-center items-center" ref={qrRef}>
                        <div className="p-2 border-2 border-slate-100 rounded-lg shadow-inner">
                            <QRCodeCanvas value={qrCode.code} size={220} level="L" />
                        </div>
                    </div>

                    {/* Stats Banner */}
                    <div className="grid grid-cols-2 border-t border-b border-slate-700 bg-slate-800">
                        <div className="p-3 text-center border-r border-slate-700">
                            <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider">Scansioni</p>
                            <p className="text-xl font-bold text-white mt-1">{qrCode.scanCount}</p>
                        </div>
                        <div className="p-3 text-center">
                            <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider">Limite Max</p>
                            <p className="text-xl font-bold text-white mt-1">
                                {qrCode.maxUses === -1 ? '∞' : qrCode.maxUses}
                            </p>
                        </div>
                    </div>

                    {/* Details List */}
                    <div className="p-5 space-y-1 bg-slate-800/50">
                        <DetailRow icon={Icons.Hash} label="ID Univoco" value={`#${qrCode.id}`} />
                        <DetailRow icon={Icons.Tag} label="Categoria" value={qrCode.category} />
                        <DetailRow icon={Icons.Check} label="Stato Attuale" value={
                            <span className={`px-2 py-0.5 rounded text-xs uppercase font-bold ${qrCode.status === 'Attivo' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                {qrCode.status}
                            </span>
                        } />

                        <DetailRow icon={Icons.Calendar} label="Inizio Validità" value={qrCode.validFrom ? formatDate(qrCode.validFrom) : "Subito"} />
                        <DetailRow icon={Icons.Calendar} label="Scadenza" value={qrCode.validTo ? formatDate(qrCode.validTo) : "Mai"} />

                        {user && ['ADMIN', 'ROLE_ADMIN', 'STAFF', 'ROLE_STAFF'].includes(user.role) && (
                            <DetailRow icon={Icons.User} label="Cliente" value={qrCode.client || 'Nessuno'} highlight />
                        )}

                        <DetailRow icon={Icons.Clock} label="Orari" value={
                            qrCode.availability && qrCode.availability.length > 0
                                ? qrCode.availability.map(slot => `${slot.from}-${slot.to}`).join(', ')
                                : 'Sempre attivo'
                        } />

                        {qrCode.maxUsesFasce !== -1 && (
                            <DetailRow icon={Icons.Activity} label="Limite Fascia" value={qrCode.maxUsesFasce.toString()} />
                        )}

                        <DetailRow icon={Icons.Calendar} label="Creato il" value={formatDate(qrCode.created)} />
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-4 bg-slate-900 border-t border-slate-700 grid grid-cols-3 gap-3">
                    <button
                        onClick={() => downloadAs('png')}
                        className="flex flex-col items-center justify-center p-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-all group"
                    >
                        <span className="text-sky-500 mb-1 group-hover:scale-110 transition-transform">{Icons.Download}</span>
                        <span className="text-xs font-bold text-slate-300">PNG</span>
                    </button>
                    <button
                        onClick={() => downloadAs('jpeg')}
                        className="flex flex-col items-center justify-center p-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-all group"
                    >
                        <span className="text-sky-500 mb-1 group-hover:scale-110 transition-transform">{Icons.Download}</span>
                        <span className="text-xs font-bold text-slate-300">JPG</span>
                    </button>
                    <button
                        onClick={downloadAsPdf}
                        className="flex flex-col items-center justify-center p-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-all group"
                    >
                        <span className="text-red-500 mb-1 group-hover:scale-110 transition-transform">{Icons.Download}</span>
                        <span className="text-xs font-bold text-slate-300">PDF</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QrCodeDisplayModal;